import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getLocations, createLocation as apiCreateLocation } from '../services/locationService';

const LocationContext = createContext();

export const useLocationContext = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationContext must be used within LocationProvider');
  return ctx;
};

export const LocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshLocations = useCallback(async () => {
    try {
      const data = await getLocations();
      const locs = data.locations || [];
      setLocations(locs);

      // Update currentLocation object if it exists in the refreshed list
      if (currentLocation) {
        const updated = locs.find((l) => l.id === currentLocation.id);
        if (updated) setCurrentLocation(updated);
      }

      return locs;
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      return [];
    }
  }, [currentLocation]);

  const switchLocation = useCallback((loc) => {
    setCurrentLocation(loc);
    localStorage.setItem('currentLocationId', String(loc.id));
  }, []);

  const addLocation = useCallback(async (data) => {
    try {
      const newLoc = await apiCreateLocation(data);
      const locs = await refreshLocations();
      const added = locs.find((l) => l.id === newLoc.id) || newLoc;
      switchLocation(added);
      return added;
    } catch (err) {
      console.error('Failed to create location:', err);
      throw err;
    }
  }, [refreshLocations, switchLocation]);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const data = await getLocations();
        const locs = data.locations || [];
        setLocations(locs);

        // Restore from localStorage or pick first
        const savedId = localStorage.getItem('currentLocationId');
        let selected = null;
        if (savedId) {
          selected = locs.find((l) => l.id === parseInt(savedId, 10));
        }
        if (!selected && locs.length > 0) {
          selected = locs[0];
        }
        if (selected) {
          setCurrentLocation(selected);
          localStorage.setItem('currentLocationId', String(selected.id));
        }
      } catch (err) {
        console.error('Failed to load locations:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Don't render children until loading is complete
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <LocationContext.Provider
      value={{
        locations,
        currentLocation,
        loading,
        switchLocation,
        addLocation,
        refreshLocations,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
